/**
 * TimePolicy — admin config for the time-card enforcement gate (Round 16).
 *
 * Available to admins + managers (the endpoint accepts admin OR timecard-admin). Three panels:
 *   1. Firm settings — global kill switch, enforcement-start date, daily minimum, weekly target.
 *   2. Member policy — per member: employment type (employee/contractor), schedule (full/part time),
 *      enforced toggle, optional per-member enforcement-start (covers new hires).
 *   3. Holidays + reopen — firm holiday calendar; reopen (unlock) or excuse a locked day.
 *
 * Only full-time EMPLOYEES get the hours gate; everyone else enforced uses attestation. A member with
 * no policy row is never gated (fail-open).
 *
 * Round 17: two more firm-wide tunables (snooze earliest time-of-day, max snoozes/day — the per-user
 * snooze itself lives on TimeGate.jsx) and a live "paused until / Resume now" state, since the old
 * "Pause enforcement" kill switch used to stay off silently forever with no way to see it was paused.
 */
import { useEffect, useState, useCallback } from 'react'
import {
  listPolicies, setPolicy, setGateSettings,
  listHolidays, addHoliday, deleteHoliday, reopenDay, excuseDay,
  listReviewAssignments, setMemberReviewers,
} from '../../services/timeGate.js'

export default function TimePolicy({ dark }) {
  const [members, setMembers] = useState([])
  const [settings, setSettings] = useState(null)
  const [holidays, setHolidays] = useState([])
  const [assignments, setAssignments] = useState({})   // reviewee_member_id -> [reviewer_member_id]
  const [loading, setLoading] = useState(true)
  const [savingId, setSavingId] = useState('')
  const [msg, setMsg] = useState('')

  const [newHolDate, setNewHolDate] = useState('')
  const [newHolName, setNewHolName] = useState('')
  const [reMember, setReMember] = useState('')
  const [reDate, setReDate] = useState('')
  const [reMsg, setReMsg] = useState('')   // inline feedback for reopen/excuse (shown by the buttons)

  const textPrim = dark ? 'rgba(207,226,232,0.92)' : '#07272D'
  const textMut  = dark ? 'rgba(91,132,148,0.80)'  : 'rgba(7,39,45,0.55)'
  const border   = dark ? 'rgba(255,255,255,0.10)' : 'rgba(7,39,45,0.12)'
  const surface  = dark ? '#0d1e24' : '#fff'

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const [p, h, a] = await Promise.all([listPolicies(), listHolidays(), listReviewAssignments()])
      setMembers(p.members || [])
      setSettings(p.settings || { enabled: false, enforcement_start_date: '', full_time_daily_min: 8, weekly_target: 40, snooze_earliest_time: '10:00', max_snoozes_per_day: 2, max_days_behind_for_snooze: 2 })
      setHolidays(h || [])
      setAssignments(a || {})
    } catch (e) { setMsg(e.message || 'Load failed') } finally { setLoading(false) }
  }, [])
  useEffect(() => { reload() }, [reload])

  function flash(t) { setMsg(t); setTimeout(() => setMsg(''), 2500) }

  async function saveSettings(patch) {
    const next = { ...settings, ...patch }
    setSettings(next)
    const r = await setGateSettings({
      enabled: next.enabled === true,
      enforcement_start_date: next.enforcement_start_date || null,
      full_time_daily_min: next.full_time_daily_min,
      weekly_target: next.weekly_target,
      snooze_earliest_time: next.snooze_earliest_time,
      max_snoozes_per_day: next.max_snoozes_per_day,
      max_days_behind_for_snooze: next.max_days_behind_for_snooze,
    })
    if (r.error) flash(r.error); else flash('Settings saved')
  }

  // Round 17 — clears an active firm-wide pause early. paused_until must be sent explicitly (even as
  // null) since the server only touches that column when the key is present in the request.
  async function resumeNow() {
    const next = { ...settings, paused_until: null }
    setSettings(next)
    const r = await setGateSettings({
      enabled: next.enabled === true,
      enforcement_start_date: next.enforcement_start_date || null,
      full_time_daily_min: next.full_time_daily_min,
      weekly_target: next.weekly_target,
      snooze_earliest_time: next.snooze_earliest_time,
      max_snoozes_per_day: next.max_snoozes_per_day,
      max_days_behind_for_snooze: next.max_days_behind_for_snooze,
      paused_until: null,
    })
    if (r.error) flash(r.error); else flash('Enforcement resumed')
  }

  async function savePolicy(m, patch) {
    const cur = m.policy || { employment_type: 'employee', schedule: 'full_time', enforced: true, enforcement_start_date: null }
    const merged = { ...cur, ...patch }
    setSavingId(m.id)
    setMembers(ms => ms.map(x => x.id === m.id ? { ...x, policy: { ...merged } } : x))
    const r = await setPolicy({
      memberId: m.id,
      employment_type: merged.employment_type,
      schedule: merged.schedule,
      enforced: merged.enforced,
      enforcement_start_date: merged.enforcement_start_date || null,
    })
    setSavingId('')
    if (r.error) flash(r.error)
  }

  // Toggle a reviewing manager on/off for a member (reviewee). Optimistic; persists the full set.
  async function toggleReviewer(revieweeId, reviewerId) {
    const cur = assignments[revieweeId] || []
    const next = cur.includes(reviewerId) ? cur.filter(x => x !== reviewerId) : [...cur, reviewerId]
    setAssignments(a => ({ ...a, [revieweeId]: next }))
    const r = await setMemberReviewers(revieweeId, next)
    if (r.error) { flash(r.error); reload() }
  }

  async function doAddHoliday() {
    if (!newHolDate) return
    const r = await addHoliday(newHolDate, newHolName)
    if (r.error) { flash(r.error); return }
    setNewHolDate(''); setNewHolName(''); reload()
  }
  async function doDeleteHoliday(id) { await deleteHoliday(id); reload() }
  async function doReopen() {
    if (!reMember || !reDate) { setReMsg('Pick a member and a date first.'); return }
    setReMsg('Reopening…')
    const r = await reopenDay(reMember, reDate)
    if (r.error) setReMsg('Could not reopen: ' + r.error)
    else if (!r.count) setReMsg('No locked day was on record for that date — nothing to reopen.')
    else setReMsg("Day reopened — unlocked for editing (stays cleared, so the gate won't re-block it).")
  }
  async function doExcuse() {
    if (!reMember || !reDate) { setReMsg('Pick a member and a date first.'); return }
    setReMsg('Excusing…')
    const r = await excuseDay(reMember, reDate, 'Excused via admin')
    setReMsg(r.error ? 'Could not excuse: ' + r.error : 'Day excused — cleared and locked, no hours expected.')
  }

  const sel = { padding: '5px 8px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, borderRadius: 5, border: `0.5px solid ${border}`, background: surface, color: textPrim }
  const inp = { ...sel, width: 130 }
  const label = { fontFamily: '"Montserrat",sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: textMut, marginBottom: 8 }
  const th = { fontFamily: '"Montserrat",sans-serif', fontSize: 9.5, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: textMut, textAlign: 'left', padding: '6px 10px' }
  const td = { fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: textPrim, padding: '6px 10px', borderTop: `0.5px solid ${border}` }

  if (loading) return <div style={{ padding: 20, fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: textMut }}>Loading…</div>

  return (
    <div style={{ padding: '16px 20px' }}>
      {msg && <div style={{ marginBottom: 12, fontFamily: '"DM Mono",monospace', fontSize: 12, color: dark ? '#7fd6a8' : '#1f9d6b' }}>{msg}</div>}

      {/* ── Firm settings ─────────────────────────────────────────── */}
      <div style={label}>Enforcement settings</div>
      {settings.paused_until && new Date(settings.paused_until) > new Date() && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, padding: '8px 12px', borderRadius: 8, background: dark ? 'rgba(180,83,9,0.15)' : '#fdf3e4', border: '0.5px solid rgba(180,83,9,0.35)' }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: '#b45309' }}>
            Paused until {new Date(settings.paused_until).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} — enforcement resumes automatically.
          </span>
          <button style={{ ...sel, cursor: 'pointer', fontWeight: 600, padding: '4px 10px' }} onClick={resumeNow}>Resume now</button>
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'flex-end', marginBottom: 24 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: textPrim }}>
          <input type="checkbox" checked={!!settings.enabled} onChange={e => saveSettings({ enabled: e.target.checked })} />
          Gate enabled (firm-wide on/off)
        </label>
        <div>
          <div style={{ fontSize: 10, color: textMut, marginBottom: 3, fontFamily: '"DM Sans",sans-serif' }}>Enforcement starts</div>
          <input type="date" style={inp} value={settings.enforcement_start_date || ''} onChange={e => saveSettings({ enforcement_start_date: e.target.value })} />
        </div>
        <div>
          <div style={{ fontSize: 10, color: textMut, marginBottom: 3, fontFamily: '"DM Sans",sans-serif' }}>Daily min (Mon–Thu)</div>
          <input type="number" step="0.5" style={{ ...inp, width: 90 }} value={settings.full_time_daily_min} onChange={e => saveSettings({ full_time_daily_min: Number(e.target.value) })} />
        </div>
        <div>
          <div style={{ fontSize: 10, color: textMut, marginBottom: 3, fontFamily: '"DM Sans",sans-serif' }}>Weekly target (Fri true-up)</div>
          <input type="number" step="0.5" style={{ ...inp, width: 90 }} value={settings.weekly_target} onChange={e => saveSettings({ weekly_target: Number(e.target.value) })} />
        </div>
        <div>
          <div style={{ fontSize: 10, color: textMut, marginBottom: 3, fontFamily: '"DM Sans",sans-serif' }}>Snoozing opens at</div>
          <input type="time" style={{ ...inp, width: 100 }} value={String(settings.snooze_earliest_time || '10:00').slice(0, 5)} onChange={e => saveSettings({ snooze_earliest_time: e.target.value })} />
        </div>
        <div>
          <div style={{ fontSize: 10, color: textMut, marginBottom: 3, fontFamily: '"DM Sans",sans-serif' }}>Max snoozes/day</div>
          <input type="number" step="1" min="0" style={{ ...inp, width: 70 }} value={settings.max_snoozes_per_day} onChange={e => saveSettings({ max_snoozes_per_day: Number(e.target.value) })} />
        </div>
        <div>
          <div style={{ fontSize: 10, color: textMut, marginBottom: 3, fontFamily: '"DM Sans",sans-serif' }}>Max days behind to snooze</div>
          <input type="number" step="1" min="0" style={{ ...inp, width: 70 }} value={settings.max_days_behind_for_snooze} onChange={e => saveSettings({ max_days_behind_for_snooze: Number(e.target.value) })} />
        </div>
      </div>
      <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: textMut, margin: '-14px 0 24px', maxWidth: 640 }}>
        Snoozing turns off entirely — no button, must resolve directly — once the oldest outstanding
        item (your own day, or the oldest entry waiting in a reviewer's queue) is older than this many
        days. This is what actually stops "snooze once a day, forever" — the daily cap alone doesn't.
      </div>

      {/* ── Member policy ─────────────────────────────────────────── */}
      <div style={label}>Member policy</div>
      <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: textMut, marginBottom: 8, maxWidth: 640 }}>
        Only full-time <strong>employees</strong> get the hours gate (≥{settings.full_time_daily_min}h Mon–Thu, week ≥{settings.weekly_target}h).
        Contractors and part-timers attest each day. A member with no policy set is never gated.
      </div>
      <div style={{ overflowX: 'auto', border: `0.5px solid ${border}`, borderRadius: 8, marginBottom: 24 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={th}>Member</th><th style={th}>Employment</th>
            <th style={th}>Schedule</th><th style={th}>Enf.</th><th style={th}>Starts</th>
          </tr></thead>
          <tbody>
            {members.map(m => {
              const p = m.policy
              return (
                <tr key={m.id} style={{ opacity: savingId === m.id ? 0.6 : 1 }}>
                  <td style={td}>
                    <div>{m.name}</div>
                    <div style={{ color: textMut, fontSize: 10.5, marginTop: 1 }}>
                      {m.email}{m.memberType ? ' · ' + m.memberType : ''}
                    </div>
                  </td>
                  <td style={td}>
                    <select style={sel} value={(p && p.employment_type) || ''} onChange={e => savePolicy(m, { employment_type: e.target.value })}>
                      <option value="">— not gated —</option>
                      <option value="employee">Employee</option>
                      <option value="contractor">Contractor</option>
                    </select>
                  </td>
                  <td style={td}>
                    <select style={sel} value={(p && p.schedule) || 'full_time'} disabled={!p} onChange={e => savePolicy(m, { schedule: e.target.value })}>
                      <option value="full_time">Full-time</option>
                      <option value="part_time">Part-time</option>
                    </select>
                  </td>
                  <td style={td}>
                    <input type="checkbox" checked={!!(p && p.enforced)} disabled={!p} onChange={e => savePolicy(m, { enforced: e.target.checked })} />
                  </td>
                  <td style={td}>
                    <input type="date" style={{ ...inp, width: 118, padding: '4px 6px' }} disabled={!p} value={(p && p.enforcement_start_date) || ''} onChange={e => savePolicy(m, { enforcement_start_date: e.target.value })} />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* ── Review responsibility ─────────────────────────────────── */}
      {(() => {
        const mgrs = members.filter(m => /principal|manager/i.test(m.memberType || ''))
        const reviewerCandidates = mgrs.length ? mgrs : members
        return (
          <>
            <div style={label}>Review responsibility</div>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: textMut, marginBottom: 8, maxWidth: 660 }}>
              Assign each person one or more reviewing managers. A manager is only gated for — and only sees —
              the people assigned to them, so the review load is split. Anyone left <strong>unassigned</strong> is
              covered by admins. (A reviewing manager must hold the Time Review module to actually review.)
            </div>
            <div style={{ overflowX: 'auto', border: `0.5px solid ${border}`, borderRadius: 8, marginBottom: 24 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Member</th><th style={th}>Reviewing manager(s)</th></tr></thead>
                <tbody>
                  {members.map(m => {
                    const revs = assignments[m.id] || []
                    return (
                      <tr key={m.id}>
                        <td style={td}>
                          <div>{m.name}</div>
                          <div style={{ color: textMut, fontSize: 10.5, marginTop: 1 }}>{m.memberType || ''}</div>
                        </td>
                        <td style={td}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {reviewerCandidates.filter(c => c.id !== m.id).map(c => {
                              const on = revs.includes(c.id)
                              return (
                                <button key={c.id} onClick={() => toggleReviewer(m.id, c.id)}
                                  style={{ padding: '3px 9px', borderRadius: 20, cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 11.5,
                                    border: `0.5px solid ${on ? '#1f9d6b' : border}`, background: on ? '#1f9d6b' : 'transparent', color: on ? '#fff' : textPrim }}>
                                  {c.name}
                                </button>
                              )
                            })}
                            {revs.length === 0 && <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: textMut, fontStyle: 'italic' }}>Unassigned — covered by admins</span>}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )
      })()}

      {/* ── Holidays + reopen ─────────────────────────────────────── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 40 }}>
        <div style={{ flex: '1 1 280px' }}>
          <div style={label}>Holidays (excused for everyone)</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            <input type="date" style={inp} value={newHolDate} onChange={e => setNewHolDate(e.target.value)} />
            <input type="text" placeholder="Name (optional)" style={{ ...inp, width: 160 }} value={newHolName} onChange={e => setNewHolName(e.target.value)} />
            <button style={{ ...sel, cursor: 'pointer', fontWeight: 600 }} onClick={doAddHoliday}>Add</button>
          </div>
          {holidays.length === 0 && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: textMut }}>No holidays set.</div>}
          {holidays.map(h => (
            <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: textPrim }}>
              <span style={{ fontFamily: '"DM Mono",monospace' }}>{h.holiday_date}</span>
              <span style={{ color: textMut }}>{h.name || ''}</span>
              <button style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer', fontSize: 12 }} onClick={() => doDeleteHoliday(h.id)}>Remove</button>
            </div>
          ))}
        </div>

        <div style={{ flex: '1 1 280px' }}>
          <div style={label}>Reopen / excuse a locked day</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 320 }}>
            <select style={sel} value={reMember} onChange={e => setReMember(e.target.value)}>
              <option value="">Select member…</option>
              {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <input type="date" style={inp} value={reDate} onChange={e => setReDate(e.target.value)} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={{ ...sel, cursor: 'pointer', fontWeight: 600 }} onClick={doReopen}>Reopen (unlock)</button>
              <button style={{ ...sel, cursor: 'pointer' }} onClick={doExcuse}>Excuse day</button>
            </div>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: textMut }}>
              Reopen unlocks the day's entries for editing (keeps it cleared). Excuse clears a day with no expectation of hours.
            </div>
            {reMsg && (
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, marginTop: 2, color: /^(Could not|No locked|Pick a)/.test(reMsg) ? '#b45309' : '#1D9E75' }}>
                {reMsg}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
